import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import moment from 'moment';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const currency = (value) => Number(value || 0).toLocaleString('pt-BR', {
  style: 'currency', currency: 'BRL'
});

export default function AppointmentFinancialCompletionModal({
  open,
  appointment,
  toast,
  onClose,
  onCompleted,
}) {
  const [methods, setMethods] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    amount: '', discount_amount: '', receive_now: true,
    payment_method_id: 'pix', payment_date: moment().format('YYYY-MM-DD'),
    due_date: moment().format('YYYY-MM-DD'), payment_type: 'single',
    installment_count: 2, notes: '',
  });

  useEffect(() => {
    if (!open || !appointment) return;
    const suggested = Number(appointment.amount ?? appointment.expected_amount ?? 0);
    setForm((previous) => ({
      ...previous,
      amount: suggested > 0 ? suggested.toFixed(2) : '',
      discount_amount: '',
      receive_now: true,
      payment_date: moment().format('YYYY-MM-DD'),
      due_date: moment().format('YYYY-MM-DD'),
      payment_type: 'single',
      installment_count: 2,
      notes: '',
    }));
    axios.get(`${API_URL}/billing/payment-methods`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      setMethods(list);
      const preferred = list.find((item) => item.id === 'pix') || list[0];
      if (preferred) setForm((previous) => ({ ...previous, payment_method_id: preferred.id }));
    }).catch((error) => console.error('Erro ao carregar formas de pagamento:', error));
  }, [open, appointment]);

  const gross = useMemo(
    () => Number(String(form.amount || '').replace(',', '.')) || 0,
    [form.amount]
  );
  const discount = useMemo(
    () => Number(String(form.discount_amount || '').replace(',', '.')) || 0,
    [form.discount_amount]
  );
  const finalAmount = Math.max(gross - discount, 0);

  if (!open || !appointment) return null;

  const submit = async (event, skipFinancial = false) => {
    event?.preventDefault?.();
    if (saving) return;
    if (!skipFinancial && gross <= 0) {
      toast?.error?.('Informe um valor maior que zero.');
      return;
    }
    if (!skipFinancial && (discount < 0 || discount >= gross)) {
      toast?.error?.('O desconto deve ser menor que o valor do atendimento.');
      return;
    }
    const method = methods.find((item) => item.id === form.payment_method_id);
    try {
      setSaving(true);
      const { data } = await axios.post(
        `${API_URL}/billing/appointments/${appointment.id}/complete-financial`,
        {
          amount: skipFinancial ? 0 : gross,
          discount_amount: skipFinancial ? 0 : discount,
          receive_now: skipFinancial ? false : form.receive_now,
          skip_financial: skipFinancial,
          due_date: skipFinancial ? null : moment(form.due_date, 'YYYY-MM-DD').startOf('day').toISOString(),
          payment_type: form.receive_now ? 'single' : form.payment_type,
          installment_count: form.receive_now ? 1 : Number(form.installment_count || 2),
          payment_method_id: form.receive_now && !skipFinancial ? form.payment_method_id : null,
          payment_method_name: form.receive_now && !skipFinancial ? method?.name : null,
          payment_date: form.receive_now && !skipFinancial
            ? moment(form.payment_date, 'YYYY-MM-DD').startOf('day').toISOString()
            : null,
          notes: form.notes.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast?.success?.(
        skipFinancial
          ? 'Atendimento finalizado sem lançamento financeiro.'
          : form.receive_now
            ? 'Atendimento finalizado e pagamento registrado!'
            : 'Atendimento finalizado e conta a receber criada!'
      );
      onCompleted?.(data);
    } catch (error) {
      toast?.error?.(error.response?.data?.detail || 'Erro ao finalizar atendimento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={(e) => {
      if (e.target === e.currentTarget && !saving) onClose?.();
    }}>
      <form onSubmit={(event) => submit(event, false)} className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white">
        <div className="flex items-start justify-between border-b p-5">
          <div>
            <h3 className="text-lg font-semibold">Finalizar atendimento</h3>
            <p className="mt-1 text-sm text-gray-500">Confirme o valor e informe se recebeu agora.</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving}><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <div className="min-h-0 space-y-4 overflow-y-auto p-5">
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
            <strong>{appointment.procedure || 'Atendimento odontológico'}</strong>
            <span className="block text-sky-700">{appointment.patient_name}</span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><label className="mb-1 block text-sm font-medium">Valor do atendimento</label><input type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} className="w-full rounded-lg border px-3 py-2" required /></div>
            <div><label className="mb-1 block text-sm font-medium">Desconto</label><input type="number" min="0" step="0.01" value={form.discount_amount} onChange={(e) => setForm((p) => ({ ...p, discount_amount: e.target.value }))} className="w-full rounded-lg border px-3 py-2" placeholder="0,00" /></div>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"><p className="text-xs text-emerald-700">Valor final</p><p className="text-lg font-bold text-emerald-900">{currency(finalAmount)}</p></div>
          <label className="flex items-center justify-between gap-4 rounded-lg border p-4"><span><span className="block font-medium">Recebeu agora?</span><span className="block text-sm text-gray-500">Se não, criaremos uma conta a receber.</span></span><input type="checkbox" checked={form.receive_now} onChange={(e) => setForm((p) => ({ ...p, receive_now: e.target.checked }))} className="h-5 w-5" /></label>
          {form.receive_now ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><label className="mb-1 block text-sm font-medium">Forma de pagamento</label><select value={form.payment_method_id} onChange={(e) => setForm((p) => ({ ...p, payment_method_id: e.target.value }))} className="w-full rounded-lg border px-3 py-2" required>{methods.map((method) => <option key={method.id} value={method.id}>{method.name}</option>)}</select></div>
            <div><label className="mb-1 block text-sm font-medium">Data do recebimento</label><input type="date" value={form.payment_date} onChange={(e) => setForm((p) => ({ ...p, payment_date: e.target.value }))} className="w-full rounded-lg border px-3 py-2" required /></div>
          </div> : <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><label className="mb-1 block text-sm font-medium">Forma da cobrança</label><select value={form.payment_type} onChange={(e) => setForm((p) => ({ ...p, payment_type: e.target.value }))} className="w-full rounded-lg border px-3 py-2"><option value="single">À vista</option><option value="installment">Parcelado</option></select></div>
            {form.payment_type === 'installment' && <div><label className="mb-1 block text-sm font-medium">Parcelas</label><input type="number" min="2" max="120" value={form.installment_count} onChange={(e) => setForm((p) => ({ ...p, installment_count: e.target.value }))} className="w-full rounded-lg border px-3 py-2" /></div>}
            <div className="sm:col-span-2"><label className="mb-1 block text-sm font-medium">{form.payment_type === 'installment' ? 'Vencimento da primeira parcela' : 'Vencimento'}</label><input type="date" value={form.due_date} onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))} className="w-full rounded-lg border px-3 py-2" required /></div>
          </div>}
          <div><label className="mb-1 block text-sm font-medium">Observações</label><textarea rows="2" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} className="w-full rounded-lg border px-3 py-2" placeholder="Opcional" /></div>
        </div>
        <div className="flex flex-col-reverse gap-2 border-t p-5 sm:flex-row sm:justify-between">
          <button type="button" onClick={(event) => submit(event, true)} disabled={saving} className="rounded-lg border px-4 py-2 text-gray-700 disabled:opacity-50">Finalizar sem lançamento financeiro</button>
          <button type="submit" disabled={saving} className="rounded-lg bg-emerald-600 px-4 py-2 font-medium text-white disabled:opacity-50">{saving ? 'Finalizando...' : form.receive_now ? 'Finalizar e receber' : 'Finalizar e criar conta a receber'}</button>
        </div>
      </form>
    </div>
  );
}
